import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Container,
  Dropdown,
  Image,
  Nav,
  Navbar,
  Spinner,
} from 'react-bootstrap';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { MenuCollapseIcon } from '../../../assets/Icon';
import { URole, getMenuItemsByRole } from '../../../constants/navigation';
import AuthContext from '../../../contexts/AuthContext';
import TokenListContext from '../../../contexts/TokenListContext';
import WalletContext from '../../../contexts/WalletContext';
import useAPI from '../../../hooks/useAPI';
import useNotificationSocket from '../../../hooks/useNotifications';
import { removeCookies } from '../../../utility/cookies';
import { formatCurrency } from '../../../utility/number.utils';
import Notifications from '../../Notifications';
import Avatar from '../Avatar';
import maiTokenImage from '../../../assets/Mai-Token.png';

const DEFAULT_PRIMARY_COLOR = '#685FD3';

interface IThemeSetting {
  logo: string;
  activeColor: string;
}

export const SettingContext = createContext({
  themeSetting: {
    logo: '',
    activeColor: DEFAULT_PRIMARY_COLOR,
  },
  setThemeSetting: ({ logo, activeColor }: IThemeSetting) => {},
});

const MainLayout = () => {
  const {
    userRole,
    userData,
    isLoading,
    setUserData,
    setUserRole,
    setAdminBalance,
    resetData,
  } = useContext(AuthContext);
  const { totalToken, tokenList } = useContext(TokenListContext);

  const [isSettingsFetched, setIsSettingsFetched] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [themeSetting, setThemeSetting] = useState<IThemeSetting>({
    logo: '',
    activeColor: DEFAULT_PRIMARY_COLOR,
  });

  const navigate = useNavigate();
  useNotificationSocket();

  const menuItems = useMemo(() => {
    if (!userRole) return [];

    let role;
    switch (userRole) {
      case URole.COMPANY_ADMIN:
        role = URole.COMPANY_ADMIN;
        break;
      case URole.ORGANIZATION:
        role = URole.ORGANIZATION;
        break;
      case URole.VENDOR:
        role = URole.VENDOR;
        break;
      case URole.VENDOR:
        role = URole.VENDOR;
        break;
      default:
        role = URole.USER;
        break;
    }

    return getMenuItemsByRole(role);
  }, [userRole, isLoading]);

  const { isLoading: isSettingsLoading } = useAPI({
    url: '/setting/get/user',
    method: 'GET',
    onSuccess: data => {
      setIsSettingsFetched(true);
      const themeData = {
        logo: data?.result?.data?.brandLogo || '',
        activeColor: data?.result?.data?.primaryColour || DEFAULT_PRIMARY_COLOR,
      };
      setThemeSetting(themeData);

      // Set CSS variables for theme colors
      document.documentElement.style.setProperty(
        '--primary',
        themeData.activeColor,
      );
      document.documentElement.style.setProperty(
        '--primary-50',
        themeData.activeColor + '40',
      );
    },
    onError: error => {
      console.error('Error fetching theme settings:', error);
    },
  });

  const logout = () => {
    removeCookies('accessToken');
    removeCookies('refreshToken');
    localStorage.clear();
    window.location.href = '/login';
  };

  // Handle window resize and close mobile menu on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when clicking sidebar link on mobile/tablet
  const handleSidebarLinkClick = () => {
    if (window.innerWidth < 1024) {
      setIsMobileMenuOpen(false);
    }
  };
  const Sidebar = () => (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      <div
        className={`sidebar pb-4 bg-white d-flex flex-column justify-content-between ${isSidebarCollapsed ? 'collapsed' : 'expanded'} ${isMobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="sidebar-content">
          <Nav className="py-2 justify-content-center">
            <Link
              to={
                userData?.role === URole.COMPANY_ADMIN
                  ? '/token-management'
                  : '/wallet'
              }
              className="ms-2"
            >
              <Image
                src={
                  themeSetting?.logo ||
                  '/assets/images/company-images/zee-logo.png'
                }
                height={43}
                width={'160px'}
                className={`object-fit-contain ${isSidebarCollapsed ? 'w-100' : ''}`}
              />
            </Link>
          </Nav>

          <div className="p-2 d-flex flex-column gap-3">
            {menuItems.map((item, index) => (
              <NavLink
                key={index}
                to={item.path}
                onClick={handleSidebarLinkClick}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
              >
                {({ isActive }) => {
                  const IconComponent = item.icon;
                  return (
                    <>
                      <IconComponent color={isActive ? '#FFF' : '#000'} />
                      <span className="text">{item.text}</span>
                    </>
                  );
                }}
              </NavLink>
            ))}
          </div>
        </div>
        {/* {!isSidebarCollapsed && (
        <div className="px-4">
          <Image src="/assets/images/company-images/kalp-studio.png" />
        </div>
      )} */}
      </div>
    </>
  );
  console.log({ userData });

  const Header = useCallback(() => {
    return (
      <Navbar
        bg="white"
        expand="lg"
        className={`border-bottom position-fixed ${isSidebarCollapsed ? 'collapsed' : 'expanded'}`}
      >
        <Container fluid>
          <Button
            variant="link"
            className="p-0 border-0 ms-2 ms-md-2"
            onClick={() => {
              if (window.innerWidth < 992) {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              } else {
                setIsSidebarCollapsed(!isSidebarCollapsed);
              }
            }}
          >
            <MenuCollapseIcon size={38} color={'var(--primary)'} />
          </Button>
          <Nav className="flex-row align-items-center flex-wrap gap-2">
            {/* {userData?.wallet?.[0]?.amount !== undefined &&
              userData?.wallet?.[0]?.amount !== null && (
                <div
                  className="px-3 d-flex my-2 align-items-center text-white rounded-pill"
                  style={{
                    fontSize: '14px',
                    gap: '8px',
                    backgroundColor:
                      themeSetting.activeColor || DEFAULT_PRIMARY_COLOR,
                    padding: '0.25rem',
                  }}
                >
                  <span>Mai Cash Balance :</span>
                  <Image
                    src={maiTokenImage}
                    height={20}
                    width={20}
                    style={{ objectFit: 'contain' }}
                    alt="MAI Token"
                  />
                  <span className="fw-bolder">
                    {formatCurrency(Number(userData?.wallet[0].amount), {
                      currency: '',
                      decimals: 0,
                    })}
                  </span>
                </div>
              )} */}

            <Nav.Link href="#" className="px-2 px-md-3">
              <Notifications />
            </Nav.Link>

            <div className="px-2 px-md-3 d-flex align-items-center">
              <Dropdown align="end">
                <Dropdown.Toggle
                  as="div"
                  className="d-flex align-items-center cursor-pointer rounded-circle bg-white"
                  style={{ cursor: 'pointer' }}
                >
                  <Avatar
                    imageUrl={userData?.image as string | undefined}
                    name={
                      (userData?.firstName || userData?.lastName
                        ? `${userData?.firstName} ${userData?.lastName}`
                        : `${userData?.email.split('')[0]} `) as string
                    }
                  />
                </Dropdown.Toggle>

                <Dropdown.Menu className="shadow-sm border-0">
                  <Dropdown.Item to="/profile" as={NavLink}>
                    Profile
                  </Dropdown.Item>
                  {/* <Dropdown.Item
                    href="#settings"
                    onClick={() => navigate('/settings')}>
                    Settings
                  </Dropdown.Item> */}
                  {/* <Dropdown.Divider /> */}
                  <Dropdown.Item onClick={resetData} className="text-danger">
                    Sign out
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Nav>
        </Container>
      </Navbar>
    );
  }, [themeSetting, isSidebarCollapsed, userData, tokenList, isMobileMenuOpen]);

  if (isLoading || isSettingsLoading || !isSettingsFetched) {
    return (
      <div className="vh-100 w-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <SettingContext.Provider value={{ themeSetting, setThemeSetting }}>
        <Sidebar />
        <div className="dashboard-content">
          <main
            className={`main-content bg-light ${isSidebarCollapsed ? 'expanded' : ''}`}
          >
            <Header />
            <div
              className="p-3 mt-5"
              style={{ minHeight: 'calc(-54px + 100vh)' }}
            >
              <Outlet />
            </div>
          </main>
        </div>
      </SettingContext.Provider>
    </div>
  );
};

export default MainLayout;
